import { FormattedDate } from 'react-intl';

import { t } from 'translations';
import { MembershipSummary, SummaryRow } from 'ui';

import { Membership, PayerType, Plan, PlanStatus, PlanType } from '../types';
import { getMonetaryValue } from '../utils/helpers';
import { isStrictNever } from '../utils/isStrictNever';
import { calculatePlanDatesIfActivatedOn } from '../utils/plans';
import { periodToProductBillingNameKey, periodToProductPlanKey } from '../utils/productLocalization';

// Type predicate to filter out plans that should be rendered
const isRenderablePlan = (
  plan: Plan,
): plan is Plan & {
  status: Exclude<PlanStatus, PlanStatus.PAST>;
  type: Exclude<PlanType, PlanType.NOT_APPLICABLE>;
  payerType: Exclude<PayerType, PayerType.NOT_APPLICABLE>;
} => {
  // Skip past plans
  if (plan.status === PlanStatus.PAST) return false;
  // Skip non-applicable plans
  if (plan.type === PlanType.NOT_APPLICABLE || plan.payerType === PayerType.NOT_APPLICABLE) return false;
  return true;
};

export const MembershipPlansSummary = ({
  membership,
  b2bPartnerName,
}: {
  membership: Membership;
  b2bPartnerName: string | null;
}) => {
  const plans = membership.active
    ? // If membership is active, just use the plans as is
      membership.plans
    : // But if membership is pending activation, the plans from api don't have dates yet so we need to guestimate them with
      // the assumption that membership is activated today.
      calculatePlanDatesIfActivatedOn(membership.plans, new Date());

  return (
    <MembershipSummary data-testid="membership-plans-summary">
      {plans
        .filter(isRenderablePlan)
        // Render plan rows
        .map((plan) => {
          const { status, periodLength, periodType, endDate, startDate, type, price, currency, payerType } = plan;
          const key = `prepaid-${status}-${startDate}`;

          // Prepare formatted values
          const formattedPrice = price && currency && getMonetaryValue(price, currency);
          const startAt =
            startDate &&
            t('membership_hub_signup_membership_summary_billing_start_date', {
              date: <FormattedDate value={startDate} format="default" />,
            });

          // Render based on plan type
          switch (type) {
            case PlanType.TRIAL: {
              return (
                <SummaryRow
                  key={key}
                  label={t('membership_hub_signup_trial_month')}
                  value={t('membership_hub_signup_free_price')}
                />
              );
            }
            case PlanType.PREPAID:
              return (
                <SummaryRow
                  key={key}
                  label={t('membership_hub_signup_X_month_prepaid_plan', { prepaidMonths: periodLength })}
                  value={t('membership_hub_signup_prepaid_applied')}
                />
              );

            case PlanType.ANNUAL:
            case PlanType.MONTHLY: {
              switch (payerType) {
                case PayerType.D2C:
                  if (typeof price !== 'number' || !startAt) return null;

                  return (
                    <SummaryRow
                      key={key}
                      label={t(periodToProductBillingNameKey(periodType))}
                      value={t(periodToProductPlanKey(periodType), {
                        price: formattedPrice,
                      })}
                      note={startAt}
                    />
                  );

                case PayerType.B2B: {
                  if (!endDate) return null;
                  const partnerName = b2bPartnerName ?? t('membership_spotlight_b2b_to_d2c_partner_fallback');

                  return (
                    <SummaryRow
                      key={key}
                      label={t('membership_hub_b2b_partner_paid_by', { partnerName })}
                      value={t('membership_hub_ending_on_date', {
                        date: <FormattedDate value={endDate} format="default" />,
                      })}
                    />
                  );
                }

                default:
                  isStrictNever(payerType);
                  return null;
              }
            }

            case PlanType.COMPENSATION:
            case PlanType.LIFETIME:
              // TODO: MMBER-10598, Design and copy missing
              return null;

            default:
              isStrictNever(type);
              return null;
          }
        })}
    </MembershipSummary>
  );
};
